import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';
import { applyModuleSeo } from '../../utils/seo';

const CANONICAL = 'https://explanatum.co.uk/modules/financial-planning';

const FinancialPlanningLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Finance Command Center | Explanatum';
    const desc = 'Forecast cash, calculate break-even points, and automate reporting with live dashboards that replace fragile spreadsheets or expensive consultants.';
    applyModuleSeo({
      title,
      description: desc,
      canonicalUrl: CANONICAL,
      keywords: [
        'financial planning software',
        'cash flow forecasting tool',
        'break-even calculator',
        'startup finance automation',
        'explanatum finance command center'
      ],
      structuredDataId: 'ld-module-financial-planning',
      softwareName: 'AI Finance Command Center',
      softwareCategory: 'FinanceApplication'
    });
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Finance command center overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-blue-100/10 px-4 py-1 text-sm font-semibold text-blue-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-cyan-300" />
                AI Finance Command Center
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Rolling cash forecasts meet break-even clarity.
                <span className="mt-2 block text-blue-600">
                  Finally see what happens before the cash runs out.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Build 13-week cash projections, model recurring revenue, and test pricing scenarios without fighting spreadsheet formulas. Explanatum keeps forecasts synced with reality and underwrites decisions consultants charge thousands to deliver.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_finance_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-cyan-500 via-indigo-500 to-emerald-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-cyan-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/finance"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open finance workspace
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Cash flow projections with recurring income & expense logic
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Break-even models, pricing tests, and contribution margins
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  Exportable reports, alerting, and AI-generated summaries
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Finance dashboard preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-100/30 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Cash Position</span>
                  <span>Scenario view</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">13-week forecast: runway 28 weeks • Net burn £12.4K • Recurring income auto-updated from subscriptions.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Scenario: hiring 2 SDRs → runway drops to 20 weeks. Break-even shifts to 412 units/month. Consultant briefing drafted.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Alerts: Supplier price increase detected. AI Business Consultant preparing new pricing tiers & investor update.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Finance clarity without drowning in tabs</h2>
            <p className="mt-4 text-base text-gray-500">Bring cash, costs, and growth scenarios into a single control panel designed for operators.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Recurring-aware cash forecasts',
              body: 'Bundle salaries, subscriptions, invoices, and ad spend into a living forecast that updates when variables change.'
            }, {
              title: 'Scenario testing in minutes',
              body: 'Clone projections, tweak headcount or pricing, and instantly see runway and margin impact without breaking formulas.'
            }, {
              title: 'Break-even intelligence',
              body: 'Model contribution margin, unit economics, and breakeven revenue for each product launch or campaign.'
            }, {
              title: 'Team-ready reporting',
              body: 'Generate dashboards, export CSVs, or trigger WhatsApp/Slack alerts when thresholds are hit.'
            }, {
              title: 'Consultant-grade narratives',
              body: 'Let the AI Business Consultant summarise cash changes, prep board updates, and suggest mitigation steps.'
            }, {
              title: 'Audit trails & history',
              body: 'Track edits, maintain version history, and benchmark scenarios without duplicating spreadsheets.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why leaders ditch spreadsheets and consultants retainers</h2>
            <p className="mt-4 text-base text-gray-500">Keep institutional knowledge in-house, iterate faster, and measure impact instantly.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Finance Command Center',
              points: [
                'Live forecasts with recurring transaction automation',
                'Scenario cloning for hiring, pricing, and fundraising plans',
                'Break-even analytics and AI-authored executive summaries',
                'Centralised reporting with exports and alerting baked-in',
                'One subscription that scales with your team'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Manual spreadsheets',
              points: [
                'Fragile formulas, version conflicts, and hidden assumptions',
                'No alerts—issues surface only after cash is gone',
                'Limited collaboration; finance lead becomes a bottleneck',
                'Historical data lives across multiple files and tabs',
                'Difficult to keep projections aligned with actuals'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Finance consultants & outsourced CFOs',
              points: [
                '£3,000+ per month for periodic decks and static models',
                'Slow turnaround when scenarios change mid-quarter',
                'Insights locked behind external expertise and billable hours',
                'Limited access to raw data and operational workflows',
                'Hand-off friction when engagements end'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-emerald-400/30 bg-emerald-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need to convince investors you survive the next 12 months?”</h3>
            <p className="mt-3 text-base text-gray-600">Spin up board-ready forecasts, break-even logic, and mitigation plans in hours—not weeks of consultant back-and-forth.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_finance_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-emerald-500 via-cyan-500 to-indigo-500 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-emerald-400/30 hover:scale-[1.02]"
              >
                Launch forecasts now
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with AI Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">How operators put finance insights to work</h2>
            <p className="mt-4 text-base text-gray-500">From subscription SaaS to retail pop-ups, Explanatum keeps decisions grounded in cash reality.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Subscription SaaS',
              body: 'Blend ARR, churn, and expansion into dynamic forecasts that highlight runway and hiring capacity.'
            }, {
              title: 'Retail & inventory-heavy teams',
              body: 'Model supplier terms, seasonal swings, and store launches with alerts before stockouts or cash crunches.'
            }, {
              title: 'Service agencies & studios',
              body: 'Track utilisation, retainer revenue, and project pipeline to understand margins and pricing levers.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default FinancialPlanningLanding;
